/**
 * PlayControl — the disc + play/pause button.
 *
 * Playback is delegated to RadioContext so the binding logic is identical to
 * v2 (src set on play, cleared on stop; loading guard; pulse glow while
 * playing). The only addition is album art: AzuraCast now supplies `art`, and
 * LOGO_URL remains the fallback exactly as in v2.
 *
 * Kept hooks from v2: data-testid="play-button".
 */

import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

export default function PlayControl({ isPlaying, isLoading, onToggle, art, logoFallback }) {
  const artwork = art || logoFallback;

  return (
    <motion.div
      className="relative mb-8"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="relative flex h-48 w-48 items-center justify-center overflow-hidden rounded-full border-2 border-[#00FF41]/30 bg-[#0D0D0D]/80 backdrop-blur-sm md:h-64 md:w-64">
        {/* Album art / logo as background */}
        <div
          className="absolute inset-0 transition-opacity duration-300 ease-out"
          style={{
            backgroundImage: `url(${artwork})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: isPlaying ? 0.8 : 0.5,
          }}
        />

        {/* Dimmer for button legibility */}
        <div
          className={`absolute inset-0 transition-all duration-300 ${
            isPlaying ? 'bg-black/20' : 'bg-black/40'
          }`}
        />

        {/* Play / pause */}
        <motion.button
          data-testid="play-button"
          type="button"
          onClick={onToggle}
          disabled={isLoading}
          aria-label={isPlaying ? 'Pause stream' : 'Play stream'}
          aria-pressed={isPlaying}
          className={`relative z-10 flex h-24 w-24 items-center justify-center rounded-full border-2 border-[#00FF41] bg-black/50 transition-all duration-300 md:h-32 md:w-32 ${
            isPlaying ? 'dmr-pulse' : 'hover:bg-[#00FF41]/10'
          }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                className="h-8 w-8 animate-spin rounded-full border-2 border-[#00FF41] border-t-transparent"
              />
            ) : isPlaying ? (
              <motion.div
                key="pause"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="flex gap-2"
              >
                <div className="h-12 w-3 rounded bg-[#00FF41] md:h-16 md:w-4" />
                <div className="h-12 w-3 rounded bg-[#00FF41] md:h-16 md:w-4" />
              </motion.div>
            ) : (
              <motion.div key="play" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                <div
                  className="ml-2 h-0 w-0"
                  style={{
                    borderLeft: '24px solid #00FF41',
                    borderTop: '16px solid transparent',
                    borderBottom: '16px solid transparent',
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      {/* Outer glow ring while playing */}
      {isPlaying && (
        <motion.div
          className="pointer-events-none absolute inset-0 rounded-full"
          animate={{
            boxShadow: [
              '0 0 60px rgba(0,255,65,0.3), 0 0 100px rgba(0,255,65,0.1)',
              '0 0 80px rgba(0,255,65,0.4), 0 0 120px rgba(0,255,65,0.2)',
              '0 0 60px rgba(0,255,65,0.3), 0 0 100px rgba(0,255,65,0.1)',
            ],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}
    </motion.div>
  );
}
